import { DateFormat, formatDate, PurchaseItemStatus } from "common";
import { Dayjs } from "dayjs";
import { SeriesOptionsType } from "highcharts";
import { GuestStatus, Seller } from "types";
import { api } from "../api/api";
import { config } from "../config";
import { AnalyticsMode } from "../stores/reports/analyticsFilterStore";

// Interface for the params
export interface ProductsReportParams {
    seller: Seller;
    from: Dayjs;
    to: Dayjs;
    mode: AnalyticsMode;
    products: ID[];
    getName?: (key: any) => string | Promise<string>;
}

const getPayload = ({ seller, from, to, mode, products }: ProductsReportParams) => {
    const filter = getBaseDemographicQuery(seller);

    const itemsFilter = filter.aggs.items.aggs.filtered;
    const itemsRange = itemsFilter.filter.bool.must;
    const query = filter.query.bool.must;
    const queryRange = query[1].nested.query.bool.must;

    const range = { from: formatDate(from, DateFormat.DATE_ISO), to: formatDate(to, DateFormat.DATE_ISO) };
    if (mode === AnalyticsMode.CASH_FLOW) {
        query.push({ range: { createdAt: range } });
    } else {
        const rangeFilter = { range: { "items.arrivalDate": range } };
        itemsRange.push(rangeFilter);
        queryRange.push(rangeFilter);
    }

    if (products.length > 0) {
        const productFilter = { terms: { "items.product.id": products } };
        itemsRange.push(productFilter);
        query.push({
            nested: {
                path: "items",
                query: productFilter,
            },
        });
    }

    return filter;
};

export const getProductReport = async (params: ProductsReportParams): Promise<SeriesOptionsType[]> => {
    const { seller, getName } = params;
    const index = config.elasticsearch.indexes.purchase;

    if (!params.from.isValid() || !params.to.isValid()) {
        return getProductResponse(null, getName!);
    }

    const { data } = await api.post(`/elasticsearch/${index}/_search?seller=${seller.id}`, getPayload(params));
    return getProductResponse(data, getName!);
};

const getBaseDemographicQuery = (seller: Seller) => {
    return {
        size: 0,
        aggs: {
            items: {
                nested: { path: "items" },
                aggs: {
                    filtered: {
                        filter: {
                            bool: {
                                must: [{ term: { "items.status": PurchaseItemStatus.CONFIRMED } }],
                                must_not: { term: { "items.guestStatus": GuestStatus.NO_SHOW } },
                            },
                        },
                        aggs: {
                            items: {
                                terms: { field: "items.product.id" },
                                aggs: {
                                    quantity: {
                                        sum: { field: "items.quantity" },
                                    },
                                },
                            },
                        },
                    },
                },
            },
        },
        query: {
            bool: {
                must: [
                    { term: { "seller.id": seller.id } },
                    { nested: { path: "items", query: { bool: { must: [] } } } },
                    {
                        nested: {
                            path: "items",
                            query: {
                                bool: {
                                    must: [{ term: { "items.status": PurchaseItemStatus.CONFIRMED } }],
                                    must_not: { term: { "items.guestStatus": GuestStatus.NO_SHOW } },
                                },
                            },
                        },
                    },
                ],
            },
        },
    } as any;
};

const getProductResponse = async (data: any, getName: (id: ID) => Promise<string> | string) => {
    if (!data?.aggregations) {
        // eslint-disable-next-line unicorn/no-useless-promise-resolve-reject
        return Promise.resolve([]);
    }

    const results = data.aggregations.items.filtered.items.buckets;

    const response = await Promise.all(
        results.map(async (r: any) => {
            const name = await getName(r.key);
            return [name, r.quantity.value];
        }),
    );

    return response;
};
